import { safeString } from "@/lib/utils";
import * as yup from "yup";

export const principalHmoIdTestError = "IDs should not match";

export const symptomsSchema = yup.object().shape({
  name: yup.string().required(),
});

export const addressFeatureSchema = yup.object({
  street: yup.string().required("Street address is required"),
  state: yup.string().required("Please select a state"),
  lga: yup.string().required("Please select an LGA"),
  isDelivery: yup.boolean().nullable(),
});

export const optionalLgaAddressFeatureSchema = yup.object({
  street: yup.string().required("Street address is required"),
  state: yup.string().required("Please select a state"),
  lga: yup.string().when("state", {
    is: (value: string) => safeString(value).toLowerCase() === "lagos",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required(),
  }),
  isDelivery: yup.boolean().nullable(),
});

// Pharmacy fields only (without address - address is handled separately)
export const pharmacyOnlySchema = yup.object({
  pharmacyCode: yup.string().nullable(),
  pharmacySource: yup.string().nullable(),
  pharmacyName: yup.string().when("pharmacyCode", {
    is: (value: string) => value && value !== "",
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  pharmacyAddress: yup.string().when("pharmacyCode", {
    is: (value: string) => value && value !== "",
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// Legacy schema with required LGA (for backward compatibility with FormSchemaType)
export const pharmacyFeatureSchema =
  pharmacyOnlySchema.concat(addressFeatureSchema);

export const followUpSchema = yup.object({
  followUpConsultationId: yup.string().when("isFollowUp", {
    is: (value: boolean) => value && value === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  doctor: yup.string().when("isFollowUp", {
    is: (value: boolean) => value && value === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  patient: yup.string().when("isFollowUp", {
    is: (value: boolean) => value && value === true,
    then: (schema) =>
      schema.required("Patient ID is required for follow-up consultations"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const hospitalSchema = yup.object({
  time: yup.string().required("Select a time for the consultation"),
  type: yup.string().required("Type of consultation is required"),
  doctor: yup.string().required("Doctor is required"),
});

export const mainSchema = yup.object({
  symptoms: yup
    .array()
    .of(symptomsSchema)
    .min(1, "Select at least one symptom.")
    .required(),

  discomfortLevel: yup
    .string()
    .oneOf(
      ["None", "Mild", "Moderate", "Severe", "Intense"],
      "This field is required.",
    )
    .required("Select a discomfort level."),

  // firstNotice: yup
  //   .string()
  //   // .oneOf(
  //   //   ["A few days ago", "Last Week", "One Month ago", "Last Year"],
  //   //   "This field is required."
  //   // )
  //   .required(),

  contactMedium: yup
    .string()
    .oneOf(["chat", "voice", "video"], "Select a contact medium.")
    .required("Select a contact medium."),

  gender: yup
    .string()
    .oneOf(["Male", "Female", "male", "female"], "Select a gender.")
    .required("Gender field is required."),

  description: yup.string(),
  image: yup.string().required(),
  hmoId: yup.string().required("HMO/Access ID is required."),
  expiryDate: yup.string().required("Expiry Date is required."),
  lastName: yup.string().required("Last Name not found!"),
  firstName: yup.string().required("First Name not found!"),
  companyId: yup.string(),
  providerId: yup.string().required("Provider ID not found!"),
  phoneNumber: yup
    .string()
    .matches(/^(?:\+?234|0)?[789][01]\d{8}$/, "Invalid phone number")
    .required("Phone number is required"),
  email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email format",
    )
    .required("Email is required"),

  dob: yup
    // .date()
    .string()
    .typeError("Select a Date to continue")
    .required(),

  consultationOwner: yup
    .mixed()
    .oneOf(["Myself", "Dependant", ""], "Select a consultation owner.")
    .required(),

  principalHmoId: yup.string().when("consultationOwner", {
    is: (value: string) => value === "Dependant",
    then: (schema) => schema.required("Principal's ID is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  isFollowUp: yup.bool().nullable(),
  createdThrough: yup.string().required("Created through is required"),
});

export const generateFullSchema = ({
  hasPharmacyFeature,
  hasFollowUpFeature,
  isHospitalConsultation,
  hasAddressFeature,
  hasOptionalLgaAddressFeature,
}: {
  hasPharmacyFeature: boolean;
  hasFollowUpFeature: boolean;
  isHospitalConsultation: boolean;
  hasAddressFeature: boolean;
  hasOptionalLgaAddressFeature: boolean;
}) => {
  let schema = mainSchema;

  if (hasPharmacyFeature) {
    schema = schema.concat(pharmacyFeatureSchema);
  }

  if (hasFollowUpFeature) {
    schema = schema.concat(followUpSchema);
  }

  if (isHospitalConsultation) {
    schema = schema.concat(hospitalSchema);
  }

  if (hasAddressFeature) {
    if (hasOptionalLgaAddressFeature) {
      schema = schema.concat(optionalLgaAddressFeatureSchema);
    } else {
      schema = schema.concat(addressFeatureSchema);
    }
  }

  return schema;
};
const fullSchema = mainSchema
  .concat(followUpSchema)
  .concat(pharmacyFeatureSchema);

export type FormSchemaType = yup.InferType<typeof fullSchema>;
